import { useEffect, useLayoutEffect } from 'react';
import { calculatePositionInRange } from './draggable-helpers.ts';

export const VIEWPORT_GAP = 16;

export type DraggablePosition = {
  left: number;
  top: number;
};

export type DraggableOffsets = {
  x?: number;
  y?: number;
};

export enum Horizontal {
  Left,
  Right,
  Center,
}
export enum Vertical {
  Top,
  Bottom,
  Center,
}
export type DraggablePlacement = {
  horizontal: Horizontal;
  vertical: Vertical;
};

type UseDraggablePositionProps = {
  placement: DraggablePlacement;
  offset?: DraggableOffsets;
  portalRoot: HTMLElement | null;
  draggableDivRef: React.RefObject<HTMLDivElement | null>;
  setPosition: React.Dispatch<React.SetStateAction<DraggablePosition | null>>;
  hidden: boolean;
};

export const useDraggablePosition = ({
  placement,
  offset,
  portalRoot,
  draggableDivRef,
  setPosition,
  hidden,
}: UseDraggablePositionProps) => {
  // Calculates draggable element position on first render.
  // It should not reset an already dragged position when the content rerenders.
  useLayoutEffect(() => {
    if (!portalRoot || !draggableDivRef.current || hidden) return;

    const draggableDiv = draggableDivRef.current;
    const draggableDivRect = draggableDiv.getBoundingClientRect();

    const leftPlacement =
      placement.horizontal === Horizontal.Left
        ? VIEWPORT_GAP
        : placement.horizontal === Horizontal.Center
          ? (window.innerWidth - draggableDivRect.width) / 2
          : window.innerWidth - draggableDivRect.width - VIEWPORT_GAP;

    const topPlacement =
      placement.vertical === Vertical.Top
        ? VIEWPORT_GAP
        : placement.vertical === Vertical.Center
          ? (window.innerHeight - draggableDivRect.height) / 2
          : window.innerHeight - draggableDivRect.height - VIEWPORT_GAP;

    const maxLeft = window.innerWidth - draggableDivRect.width - VIEWPORT_GAP;
    const maxTop = window.innerHeight - draggableDivRect.height - VIEWPORT_GAP;

    setPosition((currentPosition) => {
      if (currentPosition) {
        return currentPosition;
      }

      return {
        left: calculatePositionInRange(
          leftPlacement + (offset?.x ?? 0),
          VIEWPORT_GAP,
          maxLeft,
        ),
        top: calculatePositionInRange(
          topPlacement + (offset?.y ?? 0),
          VIEWPORT_GAP,
          maxTop,
        ),
      };
    });
  }, [
    placement.horizontal,
    placement.vertical,
    portalRoot,
    offset?.x,
    offset?.y,
    draggableDivRef,
    setPosition,
    hidden,
  ]);

  // Handles window resize and keeps draggable element inside viewport
  useEffect(() => {
    if (!portalRoot || !draggableDivRef.current) {
      return;
    }

    const draggableDiv = draggableDivRef.current;

    const onResize = () => {
      const draggableDivRect = draggableDiv.getBoundingClientRect();

      const maxLeft = window.innerWidth - draggableDivRect.width - VIEWPORT_GAP;
      const maxTop =
        window.innerHeight - draggableDivRect.height - VIEWPORT_GAP;

      setPosition((currentPosition) => {
        if (!currentPosition) {
          return currentPosition;
        }

        return {
          left: calculatePositionInRange(
            currentPosition.left,
            VIEWPORT_GAP,
            maxLeft,
          ),
          top: calculatePositionInRange(
            currentPosition.top,
            VIEWPORT_GAP,
            maxTop,
          ),
        };
      });
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [portalRoot, draggableDivRef, setPosition]);
};
